// CAPI Forwarding — server-side conversion delivery monitor (the batch-worker
// `conversions` subsystem: web events → Meta CAPI / TTD offline conversions).
// One realtime read (GET /insights/conversions-health, 30s poll) drives the
// whole page: KPI strip, per-target breakdown, signal coverage, a recent
// forwards feed, and the delivery-job history.
//
// NAMING: the repo's older "capi" (EncorePro ticket ingest) is unrelated —
// this screen is the NEW conversions/conversion_delivery subsystem; only the
// UI label says "CAPI Forwarding".

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ScrollTable } from '@/components/scroll-table';
import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { fmt } from '@/components/primitives';
import { QueryErrorNote } from '@/components/error-panel';
import { useConversionsHealth } from '@/api/hooks';
import { bandColor, bandFor, useSystemMetric } from '@/lib/system-metrics';
import { fmtDateTime } from '@/lib/datetime';
import { CONVERSION_SIGNALS } from '@/api/types.gen';
import type {
  ConversionCoverageRow,
  ConversionRecentRow,
  ConversionSummaryRow,
  JobRunSummary,
} from '@/api/types';
import { PageActions } from '@/components/page-actions';

// ─── vocab ───────────────────────────────────────────────────────────────────

const STATUSES = ['delivered', 'logged', 'pending', 'failed', 'quarantined', 'skipped'] as const;
type ConvStatus = (typeof STATUSES)[number];

const STATUS_COLOR: Record<ConvStatus, string> = {
  delivered:   'var(--good)',
  logged:      'var(--good)',
  pending:     'var(--ink-3)',
  failed:      'var(--bad)',
  quarantined: 'var(--bad)',
  skipped:     'var(--warn)',
};

const TARGET_LABEL: Record<string, string> = { meta: 'META', ttd: 'TTD' };

// Friendly labels for the shared CONVERSION_SIGNALS catalog (letter → name).
const SIGNAL_LABEL: Record<string, string> = {
  value: 'Price',
  currency: 'Currency',
  order_id: 'Order ID',
  beacon_event_id: 'Event ID',
  pii_email: 'PII email',
  pii_phone_or_name: 'PII phone/name',
  fbp: 'Meta fbp',
  fbc: 'Meta fbc',
  click_id: 'Click IDs',
  ga_ids: 'GA IDs',
  uid2: 'UID2',
  client_ip: 'IP',
  user_agent: 'UA',
};

function signalLabel(name: string): string {
  return SIGNAL_LABEL[name] ?? name;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

// The wire types use bigint for counts; JSON delivers plain numbers. Coerce
// through Number() everywhere so both are safe.
function n(v: bigint | number | null | undefined): number {
  return v == null ? 0 : Number(v);
}

function fmtAge(secs: number | null): string {
  if (secs == null) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) return `${(secs / 3600).toFixed(1)}h`;
  return `${(secs / 86400).toFixed(1)}d`;
}

// ─── delivery-halted banner ──────────────────────────────────────────────────

// The loudest thing on the page, and deliberately so. An open breaker is not a
// degradation — `conversion_delivery` has STOPPED sending to that destination
// because the provider rejected everything, and it is holding rows in `pending`
// rather than quarantining them. Nothing arrives until the fault is fixed.
//
// Driven by the server's latched state, NOT re-derived from the 24h rates in
// the KPI strip: those are averages a fresh outage barely moves, which is
// exactly why an entirely-dead TTD credential once read as healthy here.
function DeliveryHaltedBanner({ metaOpen, ttdOpen, uid2Open, since }: {
  metaOpen: boolean;
  ttdOpen: boolean;
  uid2Open: boolean;
  since: string | null;
}) {
  if (!metaOpen && !ttdOpen && !uid2Open) return null;
  if (!metaOpen && !ttdOpen) {
    // UID2 alone: TTD itself is fine, only rows that still need a UID2 lookup wait.
    return (
      <div
        role="alert"
        style={{
          padding: '10px 22px',
          borderBottom: '1px solid var(--line)',
          borderLeft: '3px solid var(--bad)',
          background: 'color-mix(in oklch, var(--bad) 12%, transparent)',
        }}
      >
        <div className="mono" style={{ fontSize: 11, color: 'var(--bad)', fontWeight: 700, letterSpacing: '.04em' }}>
          ■ UID2 MAPPING PAUSED — the UID2 service is failing every lookup.
        </div>
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.45 }}>
          Conversions that already carry UID2 tokens still send to TTD. Those that need a lookup are
          held and will send once UID2 answers again. Check the delivery job history below, then{' '}
          <Link to="/backend/alerts" preload="intent" style={{ color: 'var(--bad)', textDecoration: 'underline' }}>review the alert</Link>.
          {since ? ` Paused since ${fmtDateTime(since)}.` : ''}
        </div>
      </div>
    );
  }
  const halted = [metaOpen && 'Meta', ttdOpen && 'TTD'].filter(Boolean).join(' and ');
  const breakers = [metaOpen, ttdOpen, uid2Open].filter(Boolean).length;
  return (
    <div
      role="alert"
      style={{
        padding: '10px 22px',
        borderBottom: '1px solid var(--line)',
        borderLeft: '3px solid var(--bad)',
        background: 'color-mix(in oklch, var(--bad) 12%, transparent)',
      }}
    >
      <div className="mono" style={{ fontSize: 11, color: 'var(--bad)', fontWeight: 700, letterSpacing: '.04em' }}>
        ■ DELIVERY HALTED — {halted} {metaOpen && ttdOpen ? 'are' : 'is'} rejecting every conversion.
      </div>
      <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.45 }}>
        Sending stopped automatically to stop the queue being destroyed — quarantined rows are
        terminal and cannot be re-queued. Pending rows are being held and will deliver once this is
        fixed. Check the delivery job history below for the provider error, then{' '}
        <Link to="/backend/alerts" preload="intent" style={{ color: 'var(--bad)', textDecoration: 'underline' }}>review the alert</Link>.
        {since ? (breakers > 1 ? ` The first of these stopped at ${fmtDateTime(since)}.` : ` Halted since ${fmtDateTime(since)}.`) : ''}
      </div>
      {uid2Open && (
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--bad)', marginTop: 3, lineHeight: 1.45 }}>
          UID2 mapping is paused too: TTD conversions that still need a UID2 lookup are held until it answers again.
        </div>
      )}
    </div>
  );
}

function fmtWhen(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '—';
  return fmtDateTime(ms);
}

function fmtIso(iso: string | null): string {
  if (!iso) return '—';
  return fmtDateTime(iso);
}

// Decode the presence mask (letters from CONVERSION_SIGNALS, e.g. "VCOEF")
// into the present [letter, name] entries, catalog order.
function decodeSignals(mask: string): Array<[string, string]> {
  return CONVERSION_SIGNALS.filter(([letter]) => mask.includes(letter));
}

// ─── small pieces ────────────────────────────────────────────────────────────

function StatusPill({ status, glow }: { status: string; glow?: boolean }) {
  const color = STATUS_COLOR[status as ConvStatus] ?? 'var(--ink-3)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: glow ? `0 0 0 2px color-mix(in oklch, ${color} 25%, transparent)` : 'none' }}/>
      {status.toUpperCase()}
    </span>
  );
}

function ModeBadge({ enabled, mode }: { enabled: boolean; mode: string }) {
  const m = !enabled || mode === 'disabled'
    ? { label: 'DISABLED', color: 'var(--ink-3)' }
    : mode === 'live'
      ? { label: 'LIVE', color: 'var(--good)' }
      : { label: 'LOG-ONLY', color: 'var(--warn)' };
  return (
    <span className="mono" style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: m.color,
      border: `1px solid color-mix(in oklch, ${m.color} 40%, transparent)`,
      background: `color-mix(in oklch, ${m.color} 10%, transparent)`,
      borderRadius: 3, padding: '2px 8px',
    }}>● {m.label}</span>
  );
}

function TargetTag({ target }: { target: string }) {
  return (
    <span className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--ink-2)', border: '1px solid var(--line)', borderRadius: 3, padding: '1px 6px', textTransform: 'uppercase' }}>
      {TARGET_LABEL[target] ?? target.toUpperCase()}
    </span>
  );
}

// Per-row signal mini-chips — only the present ones, decoded from the mask.
function SignalChips({ mask }: { mask: string }) {
  const present = decodeSignals(mask);
  if (!present.length) return <span className="muted">—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap' }}>
      {present.map(([letter, name]) => (
        <span key={letter} title={signalLabel(name)} className="mono" style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '.02em', color: 'var(--ink-2)',
          background: 'var(--bg-sunk)', border: '1px solid var(--line-2)',
          borderRadius: 2, padding: '1px 4px', cursor: 'default',
        }}>{letter}</span>
      ))}
    </span>
  );
}

// Filter chip row (target / status / channel filters on the recent feed).
function ChipRow({ label, options, value, onChange }: {
  label: string;
  options: Array<{ id: string; lbl: string }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
      <span className="muted mono" style={{ fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', marginRight: 2 }}>{label}</span>
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button key={o.id} className="btn btn-sm" onClick={() => onChange(o.id)}
            style={active
              ? { background: 'var(--bg-sunk)', borderColor: 'var(--accent)', fontWeight: 700, fontSize: 10.5 }
              : { fontSize: 10.5, opacity: 0.8 }}>
            {o.lbl}
          </button>
        );
      })}
    </div>
  );
}

// ─── KPI strip ───────────────────────────────────────────────────────────────

interface KpiCell {
  key: string;
  val: ReactNode;
  sub?: ReactNode;
}

function sum24(rows: ConversionSummaryRow[], pred: (r: ConversionSummaryRow) => boolean): number {
  return rows.reduce((a, r) => a + (pred(r) ? n(r.count_24h) : 0), 0);
}

function KpiStrip({ summary, queueDepth, oldestPendingSecs, enabled, mode }: {
  summary: ConversionSummaryRow[];
  queueDepth: number;
  oldestPendingSecs: number | null;
  enabled: boolean;
  mode: string;
}) {
  const deliveredMeta = sum24(summary, (r) => r.target === 'meta' && r.status === 'delivered');
  const deliveredTtd = sum24(summary, (r) => r.target === 'ttd' && r.status === 'delivered');
  const loggedMeta = sum24(summary, (r) => r.target === 'meta' && r.status === 'logged');
  const loggedTtd = sum24(summary, (r) => r.target === 'ttd' && r.status === 'logged');
  const total = sum24(summary, () => true);
  const bad = sum24(summary, (r) => r.status === 'failed' || r.status === 'quarantined');
  const badPct = total ? (bad / total) * 100 : 0;
  // Central conversion-delivery thresholds (fixes the old 2h-vs-6h split — the
  // pending-age band now comes from the same catalog the server evaluates).
  const failM = useSystemMetric('capi.failure_quarantine_pct');
  const pendM = useSystemMetric('capi.oldest_pending_secs');
  // A stale snapshot means the evaluator stopped writing bands, so its thresholds
  // must not paint a health color — "never silently green" applies to the muted
  // tone too. Same guard the Events / Pipeline screens use.
  const badBand = failM && !failM.stale ? bandFor(failM, badPct, 'unknown') : 'unknown';
  const badColor = bandColor(badBand);
  const ageBand = pendM && !pendM.stale && oldestPendingSecs != null
    ? bandFor(pendM, oldestPendingSecs, 'unknown')
    : 'unknown';
  const ageStale = ageBand === 'warn' || ageBand === 'critical';

  const cells: KpiCell[] = [
    { key: 'Delivered 24h · Meta', val: fmt.nKpi(deliveredMeta),
      sub: loggedMeta > 0 ? <span style={{ color: 'var(--warn)' }}>+ {fmt.n(loggedMeta)} log-only</span> : <span style={{ color: 'var(--ink-3)' }}>server events sent</span> },
    { key: 'Delivered 24h · TTD', val: fmt.nKpi(deliveredTtd),
      sub: loggedTtd > 0 ? <span style={{ color: 'var(--warn)' }}>+ {fmt.n(loggedTtd)} log-only</span> : <span style={{ color: 'var(--ink-3)' }}>offline conversions</span> },
    { key: 'Failure + quarantine', val: `${badPct.toFixed(1)}%`,
      sub: <span style={{ color: badColor }}>{fmt.n(bad)} of {fmt.n(total)} · 24h</span> },
    { key: 'Queue depth', val: fmt.nKpi(queueDepth),
      sub: <span style={{ color: queueDepth > 0 ? 'var(--ink-3)' : 'var(--good)' }}>{queueDepth > 0 ? 'awaiting delivery' : 'drained'}</span> },
    { key: 'Oldest pending', val: fmtAge(oldestPendingSecs),
      sub: <span style={{ color: bandColor(ageBand) }}>{ageStale ? 'backlog — investigate' : ageBand === 'unknown' ? 'threshold status unavailable' : 'age of queue head'}</span> },
    { key: 'Mode', val: <ModeBadge enabled={enabled} mode={mode}/>,
      sub: <span style={{ color: 'var(--ink-3)' }}>{mode === 'log_only' ? 'dark launch — nothing sent' : mode === 'live' ? 'sending to providers' : 'delivery off'}</span> },
  ];
  return (
    <ResponsiveKpis><div className="kpi-strip" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
      {cells.map((c) => (
        <div className="kpi" key={c.key}>
          <div className="kpi-label">{c.key}</div>
          <div className="kpi-val">{c.val}</div>
          {c.sub && <div className="kpi-sub">{c.sub}</div>}
        </div>
      ))}
    </div></ResponsiveKpis>
  );
}

// ─── breakdown table ─────────────────────────────────────────────────────────

const SENT_7D_TITLE =
  'Conversions sent in the trailing 168 hours, by send time. Not the sales of the last 7 days: ticket sales are sent 1–2 days after they happen.';
const STATUS_7D_TITLE =
  'Rows that reached this status in the trailing 168 hours, by the time they reached it.';
const SOLD_7D_TITLE =
  'Conversions whose sale fell in the last 7 completed New York days and that reached the provider. Compare this with Meta Events Manager for the same days; Meta shows days in your own timezone, and the newest day or two fill in as each nightly batch arrives.';

export interface BreakdownRow {
  source: string;
  channel: string;
  event_type: string;
  target: string;
  counts: Record<ConvStatus, { d1: number; d7: number; sold7: number }>;
}

// Pivot summary rows: one line per source×channel×event_type×target, one
// column per status (24h, with the trailing 7 days in a muted sub-line). Both
// count by SEND time. `sold7` counts by SALE date (the last 7 completed New
// York days), the clock Meta Events Manager uses: the ticket lane sends each
// sale 26–50 h after it happened, so a send-time week is a different set of
// sales from Meta's week (2026-10-11: 129,752 sent vs Meta's 96,376 by event
// day, with no loss).
export function buildBreakdown(summary: ConversionSummaryRow[]): BreakdownRow[] {
  const byKey = new Map<string, BreakdownRow>();
  for (const r of summary) {
    const key = `${r.source}|${r.channel}|${r.event_type}|${r.target}`;
    let row = byKey.get(key);
    if (!row) {
      const counts = {} as BreakdownRow['counts'];
      for (const s of STATUSES) counts[s] = { d1: 0, d7: 0, sold7: 0 };
      row = { source: r.source, channel: r.channel, event_type: r.event_type, target: r.target, counts };
      byKey.set(key, row);
    }
    const slot = row.counts[r.status as ConvStatus];
    if (slot) { slot.d1 += n(r.count_24h); slot.d7 += n(r.count_7d); slot.sold7 += n(r.count_sale_7d); }
  }
  return [...byKey.values()].sort((a, b) =>
    a.source.localeCompare(b.source) || a.channel.localeCompare(b.channel)
    || a.event_type.localeCompare(b.event_type) || a.target.localeCompare(b.target));
}

function BreakdownTable({ summary }: { summary: ConversionSummaryRow[] }) {
  const rows = useMemo(() => buildBreakdown(summary), [summary]);
  if (!rows.length) {
    return <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>No conversion attempts recorded yet.</div>;
  }
  // Full numbers, not fmt.n's compact k/M form. The KPI strip above renders the
  // SAME quantities with fmt.nKpi, so a compact table turned "1,250 delivered"
  // into "1.3k" one panel down and read as two different figures. Consistency
  // beats narrowness here; the wrapper already scrolls on overflow.
  // Only Delivered rows were sent; every other column counts rows that reached
  // that status (queued, logged, released, refused, cancelled) in the window.
  const cellNum = (c: { d1: number; d7: number }, color: string, sent = false) => (
    <td className="num">
      <div className="mono" style={{ fontSize: 12, fontWeight: c.d1 > 0 ? 700 : 400, color: c.d1 > 0 ? color : 'var(--ink-3)' }}>{fmt.nKpi(c.d1)}</div>
      <div className="muted mono" style={{ fontSize: 9 }} title={sent ? SENT_7D_TITLE : STATUS_7D_TITLE}>
        {fmt.nKpi(c.d7)} · {sent ? 'sent 7d' : '7d'}
      </div>
    </td>
  );
  // Sales of the last 7 New York days that reached the provider, plus those
  // still waiting to be sent. The number to compare with Meta Events Manager.
  const soldCell = (r: BreakdownRow) => {
    const waiting = r.counts.pending.sold7;
    return (
      <td className="num" title={SOLD_7D_TITLE}>
        <div className="mono" style={{ fontSize: 12, fontWeight: r.counts.delivered.sold7 > 0 ? 700 : 400, color: r.counts.delivered.sold7 > 0 ? 'var(--good)' : 'var(--ink-3)' }}>
          {fmt.nKpi(r.counts.delivered.sold7)}
        </div>
        <div className="muted mono" style={{ fontSize: 9 }}>{waiting > 0 ? `${fmt.nKpi(waiting)} waiting` : 'by sale date'}</div>
      </td>
    );
  };
  return (
    <ScrollTable className="tbl-wrap" label="Conversion metrics" style={{ margin: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-sm,4px)' }}>
      <table className="tbl" style={{ margin: 0 }}>
        <thead>
          <tr>
            <th>Source</th>
            <th>Channel</th>
            <th>Event</th>
            <th>Target</th>
            <th className="num" title={SOLD_7D_TITLE}>Sold last 7 days</th>
            <th className="num">Delivered</th>
            <th className="num">Logged</th>
            <th className="num">Pending</th>
            <th className="num">Failed</th>
            <th className="num">Quarantined</th>
            <th className="num">Skipped</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const prev = rows[i - 1];
            const sameGroup = !!prev && prev.source === r.source && prev.channel === r.channel && prev.event_type === r.event_type;
            // Repeated group cells are DIMMED, not blanked. Rows sort
            // (source, channel, event_type, target), so 'meta' always precedes
            // 'ttd' within a group and the second row used to render three empty
            // cells — indistinguishable from missing data, and it read as a bug.
            // Every row now states its own dimensions.
            const dim: CSSProperties = { color: 'var(--ink-3)' };
            return (
              <tr key={`${r.source}|${r.channel}|${r.event_type}|${r.target}`} style={{ cursor: 'default' }}>
                <td className="mono" style={{ fontSize: 11, ...(sameGroup ? dim : { fontWeight: 600 }) }}>{r.source}</td>
                <td className="mono" style={{ fontSize: 11, ...(sameGroup ? dim : {}) }}>{r.channel}</td>
                <td className="mono" style={{ fontSize: 11, ...(sameGroup ? dim : {}) }}>{r.event_type}</td>
                <td><TargetTag target={r.target}/></td>
                {soldCell(r)}
                {cellNum(r.counts.delivered, 'var(--good)', true)}
                {cellNum(r.counts.logged, 'var(--good)')}
                {cellNum(r.counts.pending, 'var(--ink-1)')}
                {cellNum(r.counts.failed, 'var(--bad)')}
                {cellNum(r.counts.quarantined, 'var(--bad)')}
                {cellNum(r.counts.skipped, 'var(--warn)')}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollTable>
  );
}

// ─── signal coverage ─────────────────────────────────────────────────────────

function covColor(_pct: number): string {
  // Coverage has no server-owned threshold catalog. Show the measured value
  // without inventing an ok/warn/critical state in the browser.
  return 'var(--accent)';
}

function CoveragePanel({ coverage }: { coverage: ConversionCoverageRow[] }) {
  if (!coverage.length) {
    return <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>No rows yet — coverage appears once events queue for delivery.</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
      {coverage.map((c) => {
        const rows = n(c.rows);
        return (
          <div key={`${c.target}|${c.channel}`} className="panel" style={{ padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <TargetTag target={c.target}/>
              <span className="mono" style={{ fontSize: 11, fontWeight: 600 }}>{c.channel}</span>
              <span className="muted mono" style={{ fontSize: 10, marginLeft: 'auto' }}>{fmt.n(rows)} rows</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {CONVERSION_SIGNALS.map(([letter, name], i) => {
                const ct = n(c.signal_counts[i]);
                const pct = rows ? Math.round((ct / rows) * 100) : 0;
                return (
                  <div key={letter} title={`${signalLabel(name)}: ${fmt.n(ct)}/${fmt.n(rows)} (${pct}%)`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', width: 10, flexShrink: 0 }}>{letter}</span>
                    <span style={{ fontSize: 10.5, width: 94, flexShrink: 0, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{signalLabel(name)}</span>
                    <div style={{ flex: 1, height: 4, background: 'var(--bg-sunk)', borderRadius: 1, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: covColor(pct) }}/>
                    </div>
                    <span className="mono" style={{ fontSize: 9.5, width: 30, textAlign: 'right', color: covColor(pct) }}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── recent forwards feed ────────────────────────────────────────────────────

function RecentFeed({ recent }: { recent: ConversionRecentRow[] }) {
  const [target, setTarget] = useState('all');
  const [status, setStatus] = useState('all');
  const [channel, setChannel] = useState('all');

  const channels = useMemo(() => [...new Set(recent.map((r) => r.channel))].sort(), [recent]);
  const rows = recent.filter((r) =>
    (target === 'all' || r.target === target)
    && (status === 'all' || r.status === status)
    && (channel === 'all' || r.channel === channel));

  return (
    <>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', padding: '8px 0 10px' }}>
        <ChipRow label="Target" value={target} onChange={setTarget}
          options={[{ id: 'all', lbl: 'All' }, { id: 'meta', lbl: 'Meta' }, { id: 'ttd', lbl: 'TTD' }]}/>
        <ChipRow label="Status" value={status} onChange={setStatus}
          options={[{ id: 'all', lbl: 'All' }, ...STATUSES.map((s) => ({ id: s, lbl: s }))]}/>
        {channels.length > 1 && (
          <ChipRow label="Channel" value={channel} onChange={setChannel}
            options={[{ id: 'all', lbl: 'All' }, ...channels.map((c) => ({ id: c, lbl: c }))]}/>
        )}
      </div>
      <ScrollTable className="tbl-wrap" label="Conversion metrics" style={{ margin: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-sm,4px)' }}>
        <table className="tbl" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>Event</th>
              <th>Pixel</th>
              <th>Type</th>
              <th>Target</th>
              <th>Status</th>
              <th className="num">Attempts</th>
              <th>Signals</th>
              <th>Last error</th>
              <th>Event at · ET</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                {recent.length === 0 ? 'No forwards yet — rows appear as web events queue for delivery.' : 'No rows match these filters.'}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.source_event_id}|${r.target}`} style={{ cursor: 'default' }}>
                <td className="mono" title={`${r.source} · ${r.source_event_id}`} style={{ fontSize: 10.5, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.source_event_id}</td>
                <td className="mono" style={{ fontSize: 11 }}>{r.pixel_id || '—'}</td>
                <td className="mono" style={{ fontSize: 11 }}>{r.event_type}<div className="muted" style={{ fontSize: 9 }}>{r.channel}</div></td>
                <td><TargetTag target={r.target}/></td>
                <td>
                  <StatusPill status={r.status} glow={r.status === 'pending'}/>
                  {r.status === 'skipped' && r.reason && (
                    <div className="mono" style={{ fontSize: 9.5, color: 'var(--warn)', marginTop: 2 }}>{r.reason}</div>
                  )}
                </td>
                <td className="num mono" style={{ fontSize: 11, color: n(r.attempts) > 1 ? 'var(--warn)' : 'var(--ink-2)' }}>{n(r.attempts)}</td>
                <td><SignalChips mask={r.signals}/></td>
                <td className="mono" title={r.last_error ?? undefined} style={{ fontSize: 10, color: r.last_error ? 'var(--bad)' : 'var(--ink-3)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.last_error || '—'}
                </td>
                <td className="mono" style={{ fontSize: 10.5, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{fmtWhen(n(r.event_at))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollTable>
    </>
  );
}

// ─── delivery job history ────────────────────────────────────────────────────

function RunsTable({ runs }: { runs: JobRunSummary[] }) {
  if (!runs.length) {
    return <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>No delivery runs yet.</div>;
  }
  return (
    <ScrollTable className="tbl-wrap" label="Conversion metrics" style={{ margin: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-sm,4px)' }}>
      <table className="tbl" style={{ margin: 0 }}>
        <thead>
          <tr><th>Started · ET</th><th>Finished</th><th>Status</th><th className="num">Rows in</th><th className="num">Rows out</th></tr>
        </thead>
        <tbody>
          {runs.map((r, i) => {
            const ok = (r.status ?? '').toLowerCase() === 'ok' || (r.status ?? '').toLowerCase() === 'completed';
            const running = r.finished_at == null;
            return (
              <tr key={`${r.started_at}-${i}`} style={{ cursor: 'default' }}>
                <td className="mono" style={{ fontSize: 11 }}>{fmtIso(r.started_at)}</td>
                <td className="mono" style={{ fontSize: 11, color: running ? 'var(--accent)' : 'var(--ink-1)' }}>{running ? 'running…' : fmtIso(r.finished_at)}</td>
                <td className="mono" style={{ fontSize: 10.5, fontWeight: 600, color: running ? 'var(--accent)' : ok ? 'var(--good)' : r.status ? 'var(--bad)' : 'var(--ink-3)' }}>
                  {(r.status ?? (running ? 'running' : '—')).toUpperCase()}
                </td>
                <td className="num mono">{r.rows_in != null ? fmt.n(n(r.rows_in)) : '—'}</td>
                <td className="num mono">{r.rows_out != null ? fmt.n(n(r.rows_out)) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollTable>
  );
}

// ─── screen ──────────────────────────────────────────────────────────────────

function SectionHd({ title, note }: { title: string; note?: string }) {
  return (
    <div className="field-lbl mb-2" style={{ marginTop: 4 }}>
      {title}
      {note && <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}> {note}</span>}
    </div>
  );
}

export function CapiForwardingView({ setView: _setView }: { setView?: (v: string) => void }) {
  const { data, isLoading, isError, error, refetch } = useConversionsHealth();

  return (
    <div>
      <div className="page-hd">
        <div>
          <h1 className="page-title">CAPI Forwarding</h1>
        </div>
        <PageActions>
          {data && <ModeBadge data-phone="inline" enabled={data.enabled} mode={data.mode}/>}
        </PageActions>
      </div>

      {isLoading && !data && (
        <div className="muted" style={{ padding: 40, textAlign: 'center', fontSize: 12 }}>Loading conversion delivery health…</div>
      )}
      {isError && !data && (
        <QueryErrorNote error={error} what="conversion delivery health" onRetry={() => void refetch()}/>
      )}

      {data && !data.enabled && (
        <div style={{ padding: 22 }}>
          <div className="panel" style={{ padding: 22, textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, letterSpacing: '.02em', marginBottom: 6 }}>Conversions disabled</div>
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.6 }}>
              Server-side conversion delivery is off. Enable the <code className="mono">[conversions]</code> section
              in the SAM Server config (with a Meta conversions token under <code className="mono">[conversions.meta]</code>)
              and restart to start queueing web events for Meta CAPI / TTD.
            </div>
          </div>
        </div>
      )}

      {data && data.enabled && (
        <>
          <KpiStrip
            summary={data.summary}
            queueDepth={n(data.queue_depth)}
            oldestPendingSecs={data.oldest_pending_age_secs == null ? null : n(data.oldest_pending_age_secs)}
            enabled={data.enabled}
            mode={data.mode}
          />

          <DeliveryHaltedBanner
            metaOpen={data.meta_circuit_open}
            ttdOpen={data.ttd_circuit_open}
            uid2Open={data.uid2_circuit_open ?? false}
            since={data.circuit_open_since}
          />

          {isError && (
            <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--warn) 8%, transparent)' }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 700 }}>
                ⚠ Refresh failed — showing the last good snapshot. Retrying automatically.
              </span>
            </div>
          )}

          {data.mode === 'log_only' && (
            <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--warn) 8%, transparent)' }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 700 }}>
                ● LOG-ONLY dark launch — events are matched, built, and logged, but nothing is sent to
                {' '}{[data.meta_enabled && 'Meta', data.ttd_enabled && 'TTD'].filter(Boolean).join(' / ') || 'providers'}.
              </span>
            </div>
          )}

          <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <SectionHd title="Delivery breakdown" note="· by source × channel × event × target · each status counts rows that reached it in the last 24 h, the trailing 7 d beneath · Sold last 7 days is by sale date"/>
              <BreakdownTable summary={data.summary}/>
            </div>

            <div>
              <SectionHd title="Signal coverage" note="· share of queued rows carrying each match signal (7d)"/>
              <CoveragePanel coverage={data.coverage}/>
            </div>

            <div>
              <SectionHd title="Recent forwards" note="· newest first"/>
              <RecentFeed recent={data.recent}/>
            </div>

            <div>
              <SectionHd title="Delivery job history" note="· conversion_delivery runs"/>
              <RunsTable runs={data.recent_runs}/>
            </div>

            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.06em' }}>
              Snapshot {fmtWhen(Date.parse(data.as_of))} · age {fmtAge(Math.max(0, (Date.now() - Date.parse(data.as_of)) / 1000))} · polls every 30s · Meta {data.meta_enabled ? 'enabled' : 'off'} · TTD {data.ttd_enabled ? 'enabled' : 'off'}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
